import _ from 'underscore';
import {
  FileSizeLimits,
  acceptableImageTypes,
  acceptableVideoTypes,
} from './constants';

export class SizeLimitError extends Error {
  constructor(sizeLimit, ...params) {
    super(...params);

    this.sizeLimit = sizeLimit;
    this.name = 'SizeLimitError';
  }
}

export class MediaTypeError extends Error {
  constructor(rejectedTypeName, ...params) {
    super(...params);

    this.rejectedTypeName = rejectedTypeName;
    this.name = 'MediaTypeError';
  }
}

export class InvalidFileError extends Error {
  constructor(invalidFile, ...params) {
    super(...params);

    this.invalidFile = invalidFile;
    this.name = 'InvalidFileError';
  }
}

/**
 * Validates a file's extension and size.
 * @param {Object} file - File to validate.
 * @param {Object} [options] - List of accepted image/video types.
 * @returns {String} - File extension.
 */
export function validateFile(
  file,
  { imageTypes = acceptableImageTypes, videoTypes = acceptableVideoTypes }
  = {}
) {
  const { size, name } = file;

  const fileNameParts = name && name.split('.');
  if (_.size(fileNameParts) > 1) {
    const ext = `.${fileNameParts[fileNameParts.length - 1].toLowerCase()}`;
    if (_.intersection(acceptableVideoTypes, videoTypes).includes(ext)) {
      if (size > FileSizeLimits.Video) {
        throw new SizeLimitError(FileSizeLimits.Video);
      }
    } else if (_.intersection(acceptableImageTypes, imageTypes).includes(ext)) {
      if (ext === '.gif') {
        if (size > FileSizeLimits.Gif) {
          throw new SizeLimitError(FileSizeLimits.Gif);
        }
      } else if (size > FileSizeLimits.Image) {
        throw new SizeLimitError(FileSizeLimits.Image);
      }
    } else {
      // unknown file type
      throw new MediaTypeError(ext);
    }
    return ext;
  }
  throw new InvalidFileError(name);
}

const units = ['B', 'KB', 'MB', 'GB'];
// expect 0 <= size <= 1GB
export function fileSizeToString(size) {
  if (size === undefined || size === null || size < 0) return null;
  let unit = 0;
  while (Math.floor(size / 1024) > 0) {
    // eslint-disable-next-line no-param-reassign
    size = Math.round(size /= 1024);
    unit += 1;
  }
  return unit < units.length ? `${size} ${units[unit]}` : null;
}

export function createErrorMessage(error, i18n) {
  let msg = null;
  if (error instanceof SizeLimitError) {
    const { sizeLimit } = error;
    let unit = 'MB';
    let sizeLimitShort = sizeLimit / 1024 / 1024;
    if (sizeLimitShort >= 1024) {
      unit = 'GB';
      sizeLimitShort /= 1024;
    }

    msg = i18n.getString(_TL_('Please select a file smaller than {{sizeLimitShort}} {{unit}}.'), {
      sizeLimitShort,
      unit,
    });
  }

  if (error instanceof MediaTypeError) {
    const { rejectedTypeName } = error;
    msg = i18n.getString(_TL_('{{rejectedTypeName}} file is not supported.'), {
      rejectedTypeName,
    });
  }

  if (error instanceof InvalidFileError) {
    const { invalidFile } = error;
    msg = i18n.getString(_TL_('{{invalidFile}} is not a valid file.'), {
      invalidFile,
    });
  }

  return msg;
}
